import * as React from 'react';
import { Tooltip as TooltipPrimitive } from 'radix-ui';

import { cn } from '../../lib/utils';

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          // NOTE: text color uses the arbitrary `text-[color:…]` form (not
          // `text-background`) so tailwind-merge doesn't collapse it with the
          // custom `text-[12px]/4` size token and drop the colour (would render
          // black-on-black). See reference-figma-ds-gotchas.
          'animate-in bg-foreground fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 z-50 w-fit max-w-[min(320px,90vw)] origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-[12px]/4 text-left text-balance wrap-break-word whitespace-normal text-(--background)',
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className="bg-foreground fill-foreground z-50 size-2.5 translate-y-[calc(-50%-2px)] rotate-45 rounded-[2px]" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

interface SimpleTooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  side?: React.ComponentProps<typeof TooltipPrimitive.Content>['side'];
  /** Passed to TooltipContent — e.g. a max-width + whitespace-normal cap. */
  contentClassName?: string;
  /** Tap (touch) and Enter/Space toggle the hint. Always on for IconTooltipButton;
   *  set it for a non-button trigger that carries only a hint, such as a badge. */
  tapToToggle?: boolean;
}

/** One-liner wrapper for the common "hover to see label" pattern. */
function SimpleTooltip({ content, children, side = 'top', contentClassName, tapToToggle: tapProp }: SimpleTooltipProps) {
  const tapToToggle = tapProp || (React.isValidElement(children) && children.type === IconTooltipButton);
  const [open, setOpen] = React.useState(false);
  const touchRef = React.useRef(false);
  const focusOpenedRef = React.useRef(false);

  // The click fires right after pointerup; a scroll/drag ends in pointercancel with no click.
  const endTouch = () => {
    setTimeout(() => {
      touchRef.current = false;
    }, 0);
  };

  if (!tapToToggle) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side={side} className={contentClassName}>
          {content}
        </TooltipContent>
      </Tooltip>
    );
  }

  // Radix never opens on a touch tap, so a tap toggles the hint itself.
  return (
    <Tooltip
      open={open}
      onOpenChange={(next) => {
        if (!touchRef.current) setOpen(next);
      }}
    >
      <TooltipTrigger
        asChild
        onPointerDown={(e) => {
          touchRef.current = e.pointerType === 'touch';
        }}
        onPointerEnter={(e) => {
          if (e.pointerType !== 'touch') touchRef.current = false;
        }}
        onPointerUp={endTouch}
        onPointerCancel={endTouch}
        onClick={() => {
          if (touchRef.current) setOpen((o) => !o);
        }}
        onFocus={() => {
          focusOpenedRef.current = true;
        }}
        onBlur={() => {
          focusOpenedRef.current = false;
        }}
        onKeyDown={(e) => {
          if (!tapProp || (e.key !== 'Enter' && e.key !== ' ')) return;
          e.preventDefault();
          if (open && focusOpenedRef.current) {
            focusOpenedRef.current = false;
            return;
          }
          focusOpenedRef.current = false;
          setOpen((o) => !o);
        }}
      >
        {children}
      </TooltipTrigger>
      <TooltipContent side={side} className={contentClassName}>
        {content}
      </TooltipContent>
    </Tooltip>
  );
}

interface IconTooltipButtonProps extends Omit<React.ComponentPropsWithRef<'button'>, 'type'> {
  /** aria-label — a bare icon has no accessible name of its own. */
  label: string;
}

/** Wraps a small inline icon (e.g. an Info glyph) in a real, focusable
 *  <button> so it's reachable via Tab and announced by a screen reader —
 *  a bare <svg> passed straight to TooltipTrigger's `asChild` can never
 *  receive keyboard focus, so Radix's focus-triggered open never fires.
 *
 *  Must spread the rest of its props (and forward ref): SimpleTooltip's
 *  <TooltipTrigger asChild> clones whatever element it's given, merging in
 *  onFocus/onBlur/onPointerEnter/onPointerLeave/onClick/aria-describedby
 *  and a ref — a component that only destructures the props it cares about
 *  silently drops all of that, which breaks the tooltip on BOTH hover and
 *  focus (not just focus), since Radix relies on those injected handlers
 *  either way. */
function IconTooltipButton({ label, className, children, ...rest }: IconTooltipButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      className={cn(
        'relative inline-flex items-center justify-center rounded-full before:absolute before:-inset-1.5 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  SimpleTooltip,
  IconTooltipButton,
};
